import axios from 'axios';
import store from '../store/index';
import { postGraphqlQuery } from './GraphQLService';

function getApiBaseUrl() {
  const raw = localStorage.getItem('CF_DEFORESTATION_API_BASE_URL')
    || process.env.VUE_APP_DEFORESTRATION_API_BASE_URL
    || 'https://cf-deforestation.dimitra.dev/api';
  // strip stray wrapping quotes/whitespace so axios sees an absolute URL
  return String(raw).trim().replace(/^['"]+|['"]+$/g, '').replace(/\/+$/, '');
}

// dedicated instance so the app's default axios baseURL (/api/admin) is not prepended
const deforestationApi = axios.create();

function getAuthHeaders() {
  const token = localStorage.getItem('token') || store?.getters?.getAuthToken;
  return {
    Authorization: token,
    lang: localStorage.getItem('LANGUAGE') ?? 'en',
  };
}

function mapScoreRow(row) {
  if (!row) return row;
  return {
    ...row,
    regencyName: row.districtName ?? row.regencyName,
    importedAt: row.updatedAt ?? row.importedAt,
    firePrevention: row.firePrevention ?? row.indicators?.ind2_forestLandFires ?? null,
  };
}

async function fetchSingleByKabProfileId(kabProfileId, year = null) {
  const graphqlQuery = `
    query getIybDistrictScoreByKabProfileId($kabProfileId: Int!, $dataYear: Int) {
      getIybDistrictScoreByKabProfileId(kabProfileId: $kabProfileId, dataYear: $dataYear) {
        id
        kabProfileId
        districtName
        dataYear
        compositeScore
        firePrevention
        eudrRiskStatus
        indicators
        environmentalIndex
        socioeconomicIndex
        governanceIndex
        createdAt
        updatedAt
      }
    }
  `;
  const result = await postGraphqlQuery(graphqlQuery, {
    kabProfileId: Number(kabProfileId),
    dataYear: year || null,
  });
  return mapScoreRow(result?.data?.getIybDistrictScoreByKabProfileId);
}

export default {
  async getAll({ page = 1, limit = 10, search = '', tier = null, year = null, sortBy = 'kabProfileId', sortOrder = 'ASC' } = {}) {
    const graphqlQuery = `
      query getIybDistrictScores($filter: IybDistrictScoreFilterInput) {
        getIybDistrictScores(filter: $filter) {
          totalCount
          count
          rows {
            id
            kabProfileId
            districtName
            dataYear
            compositeScore
            firePrevention
            eudrRiskStatus
            indicators
            createdAt
            updatedAt
          }
        }
      }
    `;
    const filter = {
      page,
      limit,
      search: search || undefined,
      eudrRiskStatus: tier || undefined,
      dataYear: year || undefined,
      sortBy,
      sortOrder,
    };
    const result = await postGraphqlQuery(graphqlQuery, { filter });
    const payload = result?.data?.getIybDistrictScores;
    if (!payload) return { rows: [], totalCount: 0, count: 0 };
    return {
      ...payload,
      rows: (payload.rows || []).map(mapScoreRow),
    };
  },

  async getAvailableYears() {
    const graphqlQuery = `
      query getIybDistrictScoreYears {
        getIybDistrictScoreYears
      }
    `;
    const result = await postGraphqlQuery(graphqlQuery, {});
    const years = result?.data?.getIybDistrictScoreYears;
    return Array.isArray(years) ? years.map(y => Number(y)).filter(y => !Number.isNaN(y)) : [];
  },

  async getStats(year = null) {
    const graphqlQuery = `
      query getIybDistrictScoreStats($dataYear: Int) {
        getIybDistrictScoreStats(dataYear: $dataYear) {
          total
          green
          yellow
          red
        }
      }
    `;
    const result = await postGraphqlQuery(graphqlQuery, { dataYear: year || null });
    return result?.data?.getIybDistrictScoreStats || { total: 0, green: 0, yellow: 0, red: 0 };
  },

  async getRegencies(year = null) {
    const graphqlQuery = `
      query getIybRegencies($dataYear: Int) {
        getIybRegencies(dataYear: $dataYear) {
          kabProfileId
          districtName
        }
      }
    `;
    const result = await postGraphqlQuery(graphqlQuery, { dataYear: year || null });
    const rows = result?.data?.getIybRegencies || [];
    return rows.map(r => ({
      kabProfileId: r.kabProfileId,
      regencyName: r.districtName,
    }));
  },

  async getByKabProfileId(kabProfileId, year = null) {
    return fetchSingleByKabProfileId(kabProfileId, year);
  },

  /**
   * Load latest iyb_district_scores rows keyed by kabProfileId (farm regency from registration).
   */
  async getMapByKabProfileIds(kabProfileIds = [], year = null) {
    const ids = [...new Set(
      kabProfileIds.map(id => Number(id)).filter(id => !Number.isNaN(id) && id != null)
    )];
    if (!ids.length) return {};

    const graphqlQuery = `
      query getIybDistrictScoresByKabProfileIds($kabProfileIds: [Int!]!, $dataYear: Int) {
        getIybDistrictScoresByKabProfileIds(kabProfileIds: $kabProfileIds, dataYear: $dataYear) {
          id
          kabProfileId
          districtName
          dataYear
          compositeScore
          firePrevention
          eudrRiskStatus
          indicators
          environmentalIndex
          socioeconomicIndex
          governanceIndex
          createdAt
          updatedAt
        }
      }
    `;
    const result = await postGraphqlQuery(graphqlQuery, {
      kabProfileIds: ids,
      dataYear: year || null,
    });
    const rows = result?.data?.getIybDistrictScoresByKabProfileIds;
    if (Array.isArray(rows)) {
      const map = {};
      rows.forEach((row) => {
        if (row?.kabProfileId != null) {
          map[Number(row.kabProfileId)] = mapScoreRow(row);
        }
      });
      return map;
    }

    // Fallback when batch query is not yet available on the API
    const map = {};
    await Promise.all(ids.map(async (kabProfileId) => {
      try {
        const single = await fetchSingleByKabProfileId(kabProfileId, year);
        if (single) map[kabProfileId] = single;
      } catch (error) {
        console.error(`Failed to load IYB score for kabProfileId ${kabProfileId}`, error);
      }
    }));
    return map;
  },

  async deleteById(id) {
    const graphqlMutation = `
      mutation deleteIybDistrictScore($id: Int!) {
        deleteIybDistrictScore(id: $id)
      }
    `;
    return postGraphqlQuery(graphqlMutation, { id });
  },

  async previewFile(file, dataYear, page = 1, limit = 10) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dataYear', String(dataYear));
    formData.append('page', String(page));
    formData.append('limit', String(limit));
    const { data } = await deforestationApi.post(`${getApiBaseUrl()}/iyb-scores/preview`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
    });
    return {
      ...data,
      rows: (data.rows || []).map(mapScoreRow),
    };
  },

  async importFile(file, dataYear, onProgress) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('dataYear', String(dataYear));
    const { data } = await deforestationApi.post(`${getApiBaseUrl()}/iyb-scores/import`, formData, {
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (!onProgress || !progressEvent?.total) return;
        const pct = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        onProgress(Math.min(70, Math.max(5, pct)));
      },
    });
    return data;
  },
};
